import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useState, useEffect, type ReactNode, type ComponentType } from 'react'

type Props = {
  title: string
  icon?: ComponentType<{ className?: string }>
  children: ReactNode
  storageKey: string
  defaultExpanded?: boolean
  mobileOnly?: boolean
  headerAction?: ReactNode
}

export const CollapsibleSection = ({
  title,
  icon: Icon,
  children,
  storageKey,
  defaultExpanded = false,
  mobileOnly = false,
  headerAction,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem(storageKey)
    return stored !== null ? stored === 'true' : defaultExpanded
  })

  useEffect(() => {
    localStorage.setItem(storageKey, String(isExpanded))
  }, [isExpanded, storageKey])

  return (
    <div className="h-full flex flex-col">
      {/* Collapsible header */}
      <div className={`${mobileOnly ? 'md:hidden ' : ''}flex w-full items-center justify-between rounded-lg border border-app-line bg-app-surface px-4 py-3 text-left transition-colors hover:bg-app-subtle`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex flex-1 items-center gap-2"
        >
          {Icon && <Icon className="h-5 w-5 text-app-muted" />}
          <span className="text-sm font-semibold text-app-ink">{title}</span>
        </button>
        <div className="flex items-center gap-2">
          {headerAction}
          <button onClick={() => setIsExpanded(!isExpanded)}>
            <ChevronDownIcon
              className={`h-5 w-5 text-app-muted transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className={`${
          isExpanded ? 'mt-3' : 'hidden'
        } ${mobileOnly ? 'md:block md:mt-0 ' : ''}h-full flex-1`}
      >
        {children}
      </div>
    </div>
  )
}
